/* global grecaptcha */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { selectFormalQuote } from 'formal_quote/selectors';
import { createFormalQuote } from 'formal_quote/thunks';
import { FORMAL_QUOTE_OPTIONS } from 'formal_quote/options';
import { validateForm } from 'formal_quote/validator';
import { useFormInitialization } from 'formal_quote/hooks/useFormInitialization';
import { useAddressCountries } from 'formal_quote/hooks/useAddressCountries';
import SuccessModal from 'formal_quote/components/SuccessModal';
import RecommendedProduct from 'formal_quote/components/RecommendedProduct';
import LicenseScopeCards from 'formal_quote/components/LicenseScopeCards';
import AddressFields from 'formal_quote/components/AddressFields';
import ToasterComponent from 'shared/components/ToasterComponent';
import {
  setFormData,
  setErrors,
  clearErrors,
  showSuccessModal,
  resetForm,
  setRecaptchaCompleted,
} from 'formal_quote/slice';
import SerialNumberInput from './SerialNumberInput';

const FormalQuoteForm = () => {
  const dispatch = useDispatch();
  const { formData, errors, loading, recaptchaCompleted } = useSelector(selectFormalQuote);
  const recaptchaWidgetId = useRef(null);
  const countries = useAddressCountries();

  // Initialize form from URL parameters
  useFormInitialization({ paramName: 'software' });

  // Conditional visibility logic
  const shouldShowSoftwareType = formData.looking_for === 'new_license';
  const shouldShowSerialNumbers = formData.looking_for === 'renew_license';
  const shouldShowNumberOfUsers = formData.looking_for === 'new_license';

  const shouldShowPortability =
    formData.looking_for === 'new_license' &&
    formData.license_type !== 'network';
  const shouldShowDeliveryMethod = formData.looking_for === 'new_license';
  const shouldShowDeliveryAddress = !formData.same_as_billing;
  const isViewmateBusiness = formData.software_type === 'viewmate_business';
  const shouldShowLicenseScope = isViewmateBusiness && shouldShowNumberOfUsers;

  // Preserve values
  const [preservedSerialNumbers, setPreservedSerialNumbers] = useState('');
  const [preservedSoftwareType, setPreservedSoftwareType] = useState('');
  const [preservedNumberOfUsers, setPreservedNumberOfUsers] = useState('');
  const [preservedLicenseType, setPreservedLicenseType] = useState('');
  const [preservedPortability, setPreservedPortability] = useState('');
  const [preservedDeliveryAddress, setPreservedDeliveryAddress] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_country: '',
    delivery_state: '',
    delivery_country_region: '',
    delivery_zip_code: '',
  });

  useEffect(() => {
    // Set values to null when groups are hidden, but preserve them first
    if (!shouldShowSoftwareType && formData.software_type) {
      setPreservedSoftwareType(formData.software_type);
      dispatch(setFormData({ software_type: null }));
    }

    if (
      formData.license_type === 'network' &&
      formData.portability_option !== 'not_needed' &&
      !shouldShowLicenseScope
    ) {
      if (formData.portability_option) {
        setPreservedPortability(formData.portability_option);
      }
      dispatch(setFormData({ portability_option: 'not_needed' }));
    }

    if (!shouldShowNumberOfUsers && formData.number_of_users) {
      setPreservedNumberOfUsers(formData.number_of_users);
      dispatch(setFormData({ number_of_users: null }));
    }

    if (!shouldShowNumberOfUsers && formData.license_type) {
      setPreservedLicenseType(formData.license_type);
      dispatch(setFormData({ license_type: null }));
    }

    if (!shouldShowSerialNumbers && formData.serial_numbers) {
      setPreservedSerialNumbers(formData.serial_numbers);
      dispatch(setFormData({ serial_numbers: null }));
    }

    if (!shouldShowDeliveryMethod && formData.delivery_method) {
      dispatch(setFormData({ delivery_method: null }));
    }
  }, [
    shouldShowSoftwareType,
    shouldShowPortability,
    shouldShowLicenseScope,
    shouldShowNumberOfUsers,
    shouldShowSerialNumbers,
    shouldShowDeliveryMethod,
    formData.software_type,
    formData.portability_option,
    formData.number_of_users,
    formData.license_type,
    formData.serial_numbers,
    formData.delivery_method,
    dispatch,
  ]);

  useEffect(() => {
    // Set delivery method based on conditions
    if (formData.portability_option === 'not_needed' || formData.license_type === 'network') {
      dispatch(setFormData({ delivery_method: 'instant_download' }));
    }

    if (formData.portability_option === 'any_machine' && formData.license_type === 'standalone') {
      dispatch(setFormData({ delivery_method: 'ground_shipping' }));
    }
  }, [formData.portability_option, formData.license_type, dispatch]);

  // Restore preserved fields when switching back
  useEffect(() => {
    if (shouldShowSerialNumbers && preservedSerialNumbers && formData.serial_numbers === null) {
      dispatch(setFormData({ serial_numbers: preservedSerialNumbers }));
    }
  }, [shouldShowSerialNumbers, preservedSerialNumbers, formData.serial_numbers, dispatch]);

  useEffect(() => {
    if (shouldShowSoftwareType && preservedSoftwareType && !formData.software_type) {
      dispatch(setFormData({ software_type: preservedSoftwareType }));
    }
  }, [shouldShowSoftwareType, preservedSoftwareType, formData.software_type, dispatch]);

  useEffect(() => {
    if ( shouldShowNumberOfUsers && preservedNumberOfUsers && !formData.number_of_users) {
      dispatch(setFormData({ number_of_users: preservedNumberOfUsers }));
    }
  }, [shouldShowNumberOfUsers, preservedNumberOfUsers, formData.number_of_users, dispatch]);

  // Render reCAPTCHA checkbox
  useEffect(() => {
    const checkGrecaptcha = () => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
          recaptchaWidgetId.current = grecaptcha.render('recaptcha-checkbox', {
            sitekey: Spree.recaptchaSiteKey,
            callback: () => {
              dispatch(setRecaptchaCompleted(true));
            }
          });
        });
      } else {
        setTimeout(checkGrecaptcha, 100);
      }
    };

    checkGrecaptcha();
  }, [dispatch]);

  useEffect(() => {
    if (
      shouldShowNumberOfUsers &&
      formData.license_scope !== 'site' &&
      preservedLicenseType &&
      !formData.license_type
    ) {
      dispatch(setFormData({ license_type: preservedLicenseType }));
    }
  }, [
    shouldShowNumberOfUsers,
    preservedLicenseType,
    formData.license_scope,
    formData.license_type,
    dispatch,
  ]);

  useEffect(() => {
    if (shouldShowPortability && preservedPortability) {
      dispatch(setFormData({ portability_option: preservedPortability }));
      setPreservedPortability('');
    }
  }, [shouldShowPortability, preservedPortability, formData.portability_option, dispatch]);

  // Keep ViewMate Business scope values isolated from every other product.
  useEffect(() => {
    if (shouldShowLicenseScope) {
      const scopeData = {};

      if (!formData.license_scope) scopeData.license_scope = 'standard';
      if (formData.portability_option !== 'not_needed') {
        scopeData.portability_option = 'not_needed';
      }
      if (!formData.license_type) {
        scopeData.license_type = 'standalone';
      }

      if (Object.keys(scopeData).length) {
        dispatch(setFormData(scopeData));
      }
    } else if (formData.license_scope) {
      dispatch(
        setFormData({
          license_scope: null,
          license_type: formData.license_type || 'standalone',
        }),
      );
    }
  }, [
    shouldShowLicenseScope,
    formData.license_scope,
    formData.license_type,
    formData.portability_option,
    dispatch,
  ]);

  // Same as billing address toggle
  useEffect(() => {
    if (formData.same_as_billing) {
      // Save current delivery address values before copying
      setPreservedDeliveryAddress({
        delivery_address: formData.delivery_address || '',
        delivery_city: formData.delivery_city || '',
        delivery_country: formData.delivery_country || '',
        delivery_state: formData.delivery_state || '',
        delivery_country_region: formData.delivery_country_region || '',
        delivery_zip_code: formData.delivery_zip_code || '',
      });

      // Copy billing address to delivery address
      dispatch(
        setFormData({
          delivery_address: formData.billing_address,
          delivery_city: formData.billing_city,
          delivery_country: formData.billing_country,
          delivery_state: formData.billing_state,
          delivery_country_region: formData.billing_country_region,
          delivery_zip_code: formData.billing_zip_code,
        }),
      );
    } else {
      // Restore preserved delivery address values
      dispatch(
        setFormData({
          delivery_address: preservedDeliveryAddress.delivery_address,
          delivery_city: preservedDeliveryAddress.delivery_city,
          delivery_country: preservedDeliveryAddress.delivery_country,
          delivery_state: preservedDeliveryAddress.delivery_state,
          delivery_country_region: preservedDeliveryAddress.delivery_country_region,
          delivery_zip_code: preservedDeliveryAddress.delivery_zip_code,
        }),
      );
    }
  }, [formData.same_as_billing, dispatch]);

  // Real-time copy billing to delivery when checkbox is checked
  useEffect(() => {
    if (formData.same_as_billing) {
      dispatch(
        setFormData({
          delivery_address: formData.billing_address || '',
          delivery_city: formData.billing_city || '',
          delivery_country: formData.billing_country || '',
          delivery_state: formData.billing_state || '',
          delivery_country_region: formData.billing_country_region || '',
          delivery_zip_code: formData.billing_zip_code || '',
        }),
      );
    }
  }, [
    formData.billing_address,
    formData.billing_city,
    formData.billing_country,
    formData.billing_state,
    formData.billing_country_region,
    formData.billing_zip_code,
    formData.same_as_billing,
    dispatch,
  ]);

  // Update preserved values when delivery address fields change
  useEffect(() => {
    if (!formData.same_as_billing) {
      setPreservedDeliveryAddress({
        delivery_address: formData.delivery_address || '',
        delivery_city: formData.delivery_city || '',
        delivery_country: formData.delivery_country || '',
        delivery_state: formData.delivery_state || '',
        delivery_country_region: formData.delivery_country_region || '',
        delivery_zip_code: formData.delivery_zip_code || '',
      });
    }
  }, [
    formData.delivery_address,
    formData.delivery_city,
    formData.delivery_country,
    formData.delivery_state,
    formData.delivery_country_region,
    formData.delivery_zip_code,
    formData.same_as_billing,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFormData({ [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      dispatch(clearErrors());
    }
  };

  const handleCountryChange = (prefix) => (event) => {
    const countryField = `${prefix}_country`;
    const stateField = `${prefix}_state`;
    dispatch(setFormData({ [countryField]: event.target.value, [stateField]: '' }));
    if (errors[countryField] || errors[stateField]) {
      dispatch(clearErrors());
    }
  };

  const handleLicenseScopeChange = (e) => {
    const { value } = e.target;
    const usersCount = Number(formData.number_of_users);
    // eslint-disable-next-line no-nested-ternary
    const numberOfUsers = value === 'site'
      ? (Number.isInteger(usersCount) && usersCount >= 10 ? formData.number_of_users : '10')
      : (Number.isInteger(usersCount) && usersCount >= 1 && usersCount <= 5 ? formData.number_of_users : '5');

    dispatch(setFormData({ license_scope: value, number_of_users: numberOfUsers }));
    if (errors.license_scope || errors.number_of_users) {
      dispatch(clearErrors());
    }
  };

  const onSubmit = async () => {
    const newErrors = validateForm(formData, {
      shouldShowSoftwareType,
      shouldShowSerialNumbers,
      shouldShowNumberOfUsers,
      shouldShowPortability,
      shouldShowDeliveryMethod,
      shouldShowLicenseScope,
    }, countries);

    if (Object.keys(newErrors).length > 0) {
      dispatch(setErrors(newErrors));
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      await dispatch(
        createFormalQuote({
          recaptchaWidgetId: recaptchaWidgetId.current,
          formal_quote: {
            ...formData,
          },
        }),
      ).unwrap();

      dispatch(resetForm());
      dispatch(setRecaptchaCompleted(false));
      // Clear all preserved values
      setPreservedSerialNumbers('');
      setPreservedSoftwareType('');
      setPreservedNumberOfUsers('');
      setPreservedLicenseType('');
      setPreservedPortability('');
      dispatch(showSuccessModal());
      setPreservedDeliveryAddress({
        delivery_address: '',
        delivery_country_region: '',
        delivery_zip_code: '',
      });

      if (grecaptcha?.reset && recaptchaWidgetId.current !== null) {
        grecaptcha.reset(recaptchaWidgetId.current);
      }
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('Formal quote submission error:', ex);

      dispatch(setRecaptchaCompleted(false));
      if (
        typeof grecaptcha !== 'undefined' &&
        grecaptcha.reset &&
        recaptchaWidgetId.current !== null
      ) {
        grecaptcha.reset(recaptchaWidgetId.current);
      }

      if (ex?.errors && !Array.isArray(ex.errors)) {
        const serverErrors = Object.keys(ex.errors).reduce((fieldErrors, field) => ({
          ...fieldErrors,
          [field]: Array.isArray(ex.errors[field]) ? ex.errors[field].join(', ') : ex.errors[field],
        }), {});
        dispatch(setErrors(serverErrors));
      }

      if (ex?.error) {
        toast.error(ex.error);
      } else {
        toast.error('An error occurred. Please try again later.');
      }
    }
  };

  const renderField = (name, label, type = 'text', required = true) => (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <input
        type={type}
        id={name}
        disabled={loading}
        name={name}
        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
        value={formData[name]}
        onChange={handleInputChange}
        required={required}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  const renderCheckbox = (name, label) => (
    <div className="form-check">
      <input
        type="checkbox"
        id={name}
        name={name}
        disabled={loading}
        className="form-check-input"
        checked={formData[name] || false}
        onChange={(e) => {
          const { name: fieldName, checked } = e.target;
          dispatch(setFormData({ [fieldName]: checked }));
        }}
      />
      <label htmlFor={name} className="form-check-label text-nowrap">
        {label}
      </label>
    </div>
  );

  const renderSelect = (name, label, options, required = true) => (
    <div className="form-group">
      <label htmlFor={name} className="checks-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <select
        id={name}
        disabled={loading}
        name={name}
        className={`form-control${errors[name] ? ' is-invalid' : ''}`}
        value={formData[name] || ''}
        onChange={handleInputChange}
        required={required}
      >
        {Object.entries(options).map(([value, optionLabel]) => (
          <option key={value} value={value}>
            {optionLabel}
          </option>
        ))}
      </select>
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  const renderRadioGroup = (name, label, options, showIcons = false) => {
    const getColumnClass = () => {
      if (showIcons) return 'col-12 col-sm-6';
      if (options.length === 2) return 'col-12 col-sm-5';
      return 'col-12 col-sm-4';
    };

    return (
      <div className="form-group">
        <div className="container-fluid d-flex flex-column flex-md-row px-0 align-items-center">
          <div className="checks-label">{label}</div>
          <div
            className={`row w-100 ${showIcons ? 'images-row' : 'checks-row'}`}
          >
            {options.map((option) => (
              <div
                key={option.value}
                className={`${getColumnClass()} d-flex align-items-center px-md-0`}
              >
                {showIcons && option.icon && (
                  <div className="icon-container">
                    <img
                      src={Spree.image_assets[option.icon]}
                      className="img-fluid"
                      alt={option.label}
                    />
                  </div>
                )}
                <div className="form-check">
                  <input
                    type="radio"
                    id={`${name}_${option.value}`}
                    name={name}
                    disabled={loading}
                    value={option.value}
                    className={`form-check-input ${errors[name] ? 'is-invalid' : ''}`}
                    checked={formData[name] === option.value}
                    onChange={handleInputChange}
                    aria-labelledby={`${name}_${option.value}_label`}
                  />
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor={`${name}_${option.value}`}
                    className="form-check-label"
                    id={`${name}_${option.value}_label`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: option.label }}
                  />
                  {option.badge && (
                    <span className="badge">{option.badge}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {errors[name] && (
          <div className="invalid-feedback d-block">{errors[name]}</div>
        )}
      </div>
    );
  };

  return (
    <div className="formal-quote-form">
      {renderRadioGroup('looking_for', 'Looking for', [
        {
          value: 'new_license',
          label: FORMAL_QUOTE_OPTIONS.looking_for.new_license,
        },
        {
          value: 'renew_license',
          label: FORMAL_QUOTE_OPTIONS.looking_for.renew_license,
        },
      ])}

      {shouldShowSoftwareType &&
        renderRadioGroup(
          'software_type',
          'Software',
          [
            {
              value: 'viewmate_business',
              label: FORMAL_QUOTE_OPTIONS.software_type.viewmate_business,
              icon: 'viewmate_business_icon',
            },
            {
              value: 'viewmate_essentials',
              label: FORMAL_QUOTE_OPTIONS.software_type.viewmate_essentials,
              icon: 'viewmate_essentials_icon',
            },
            {
              value: 'viewmate_professional',
              label: FORMAL_QUOTE_OPTIONS.software_type.viewmate_professional,
              icon: 'viewmate_professional_icon',
            },
            {
              value: 'CAMMaster Designer',
              label: FORMAL_QUOTE_OPTIONS.software_type['CAMMaster Designer'],
              icon: 'cammaster_designer_icon',
            },
          ],
          true,
        )}

      {shouldShowSerialNumbers && (
        <div className="row">
          <div className="col-12">
            <SerialNumberInput
              name="serial_numbers"
              label="Serial Numbers to Renew"
              value={formData.serial_numbers || ''}
              onChange={(value) =>
                dispatch(setFormData({ serial_numbers: value }))
              }
              error={errors.serial_numbers}
              disabled={loading}
            />
          </div>
        </div>
      )}

      {shouldShowLicenseScope && (
        <>
          <LicenseScopeCards
            value={formData.license_scope}
            onChange={handleLicenseScopeChange}
            disabled={loading}
            error={errors.license_scope}
          />
          <LicenseScopeCards
            value={formData.license_type}
            onChange={handleInputChange}
            disabled={loading}
            error={errors.license_type}
            inputName="license_type"
            legend="License Type"
            options={FORMAL_QUOTE_OPTIONS.viewmate_business_license_type}
          />
          <div className="row align-items-end">
            <div className="col-12 col-md-6">
              <div className="form-group">
                <label htmlFor="number_of_users" className="form-label">
                  {formData.license_scope === 'site'
                    ? 'Potential Number of Users'
                    : 'Number of Licenses'}{' '}
                  <span className="text-danger">*</span>
                </label>
                <p className="formal-quote-field-description font-medium">
                  {formData.license_scope === 'site'
                    ? 'Organization-wide coverage is available in flexible user ranges for businesses of different sizes.'
                    : 'Choose the number of standalone or network licenses needed.'}
                </p>
                {formData.license_scope === 'site' ? (
                  <input
                    type="number"
                    id="number_of_users"
                    name="number_of_users"
                    min="10"
                    disabled={loading}
                    className={`form-control ${errors.number_of_users ? 'is-invalid' : ''}`}
                    value={formData.number_of_users || ''}
                    onChange={handleInputChange}
                    required
                  />
                ) : (
                  <select
                    id="number_of_users"
                    name="number_of_users"
                    disabled={loading}
                    className={`form-control ${errors.number_of_users ? 'is-invalid' : ''}`}
                    value={formData.number_of_users || ''}
                    onChange={handleInputChange}
                    required
                  >
                    {Object.entries(FORMAL_QUOTE_OPTIONS.viewmate_business_number_of_users).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                )}
                {errors.number_of_users && (
                  <div className="invalid-feedback">{errors.number_of_users}</div>
                )}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="w-100">{renderField('company_name', 'Company Name')}</div>
            </div>
          </div>
        </>
      )}

      {shouldShowNumberOfUsers && !isViewmateBusiness && (
        <div className="row align-items-center">
          <div className="col-12 col-md-6">
            {renderSelect(
              'number_of_users',
              'Number of Users',
              FORMAL_QUOTE_OPTIONS.number_of_users,
              false,
            )}
          </div>
          <div className="col-12 col-md-6 d-flex align-items-end">
            <div className="form-group">
              <div className="form-check mt-1">
                <input
                  type="checkbox"
                  id="license_type"
                  name="license_type"
                  disabled={loading}
                  className="form-check-input"
                  checked={formData.license_type === 'network'}
                  onChange={({ target }) =>
                    dispatch(
                      setFormData({
                        license_type: target.checked ? 'network' : 'standalone',
                      }),
                    )
                  }
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="license_type" className="checks-label">
                  Share license with my team
                </label>
                <p className="text-muted mb-0">
                  Allow multiple team members to use <br /> the same license
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {shouldShowPortability && !isViewmateBusiness &&
        renderRadioGroup('portability_option', 'Portability', [
          {
            value: 'not_needed',
            label: FORMAL_QUOTE_OPTIONS.portability_option.not_needed,
          },
          {
            value: 'any_machine',
            label: FORMAL_QUOTE_OPTIONS.portability_option.any_machine,
            badge: 'Great for Hybrid workers!',
          },
        ])}

      {shouldShowDeliveryMethod && !isViewmateBusiness &&
        (() => {
          let deliveryOptions;
          if (
            formData.license_type === 'network' ||
            formData.portability_option === 'not_needed'
          ) {
            deliveryOptions = [
              {
                value: 'instant_download',
                label: FORMAL_QUOTE_OPTIONS.delivery_method.instant_download,
              },
            ];
          } else {
            deliveryOptions = [
              {
                value: 'ground_shipping',
                label: FORMAL_QUOTE_OPTIONS.delivery_method.ground_shipping,
              },
              {
                value: '2_day_shipping',
                label: FORMAL_QUOTE_OPTIONS.delivery_method['2_day_shipping'],
              },
            ];
          }
          return renderRadioGroup(
            'delivery_method',
            'Delivery Method',
            deliveryOptions,
          );
        })()}

      <div className="row mt-5">
        <div className="col-md-6">
          {renderField('recipient_name', 'Recipient Name')}
        </div>
        <div className="col-md-6">{renderField('email', 'Email', 'email')}</div>
      </div>

      <div className="row">
        <div className="col-md-6">
          {isViewmateBusiness
            ? renderField('job_title', 'Job Title', 'text', false)
            : renderField('company_name', 'Company Name')}
        </div>
        <div className="col-md-6">
          {renderField('phone_number', 'Phone Number', 'tel')}
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-6 mt-md-3">
          <div className="checks-label my-3">Billing Address</div>
          <AddressFields
            prefix="billing"
            countries={countries}
            formData={formData}
            errors={errors}
            loading={loading}
            onChange={handleInputChange}
            onCountryChange={handleCountryChange('billing')}
          />
        </div>

        <div className="col-12 col-md-6 mt-md-3">
          <div className="d-flex align-items-center justify-content-between my-3">
            <div className="checks-label">Delivery Address</div>
            {renderCheckbox('same_as_billing', 'Same as Billing Address')}
          </div>

          {shouldShowDeliveryAddress && (
            <AddressFields
              prefix="delivery"
              countries={countries}
              formData={formData}
              errors={errors}
              loading={loading}
              onChange={handleInputChange}
              onCountryChange={handleCountryChange('delivery')}
            />
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <RecommendedProduct />
        </div>
      </div>

      <div className="row">
        <div className="col-12 text-center mt-5">
          <div className="mb-3 d-flex justify-content-center">
            <div id="recaptcha-checkbox" data-sitekey={Spree.recaptchaSiteKey} />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={loading || !recaptchaCompleted}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
          <p className="text-muted mt-5">
            *The quotation will be processed in the next 12 hours
          </p>
        </div>
      </div>
      <SuccessModal />
      <ToasterComponent />
    </div>
  );
};

export default FormalQuoteForm;
